let notificationPermissionRequest = null;
let callRingtone = null;
const activeNotifications = new Map();

const getAudioContextCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.AudioContext || window.webkitAudioContext || null;
};

const getNotificationCtor = () => {
  if (typeof window === 'undefined') return null;
  return window.Notification || null;
};

const canNotify = () => Boolean(getNotificationCtor());

const getMessengerNotificationPermission = () => {
  if (!canNotify()) return 'unsupported';
  return getNotificationCtor().permission;
};

const requestMessengerNotificationPermission = ({ retryBlocked = false } = {}) => {
  if (!canNotify()) return Promise.resolve('unsupported');
  const NotificationCtor = getNotificationCtor();
  if (NotificationCtor.permission === 'granted') return Promise.resolve(NotificationCtor.permission);
  if (NotificationCtor.permission === 'denied' && !retryBlocked) {
    return Promise.resolve(NotificationCtor.permission);
  }
  if (!notificationPermissionRequest) {
    notificationPermissionRequest = NotificationCtor.requestPermission()
      .catch(() => 'denied')
      .finally(() => {
        notificationPermissionRequest = null;
      });
  }
  return notificationPermissionRequest;
};

const focusMessenger = () => {
  if (typeof window === 'undefined') return;
  window.focus?.();
  window.dispatchEvent(new CustomEvent('responder-messenger-open'));
};

const showMessengerNotification = async ({ title, body, tag, requireInteraction = false }) => {
  if (!canNotify()) return null;
  const NotificationCtor = getNotificationCtor();
  if (NotificationCtor.permission !== 'granted') return null;

  const notification = new NotificationCtor(title, {
    body,
    tag,
    requireInteraction,
    renotify: Boolean(tag),
    silent: true,
  });

  notification.onclick = () => {
    focusMessenger();
    notification.close();
  };

  notification.onclose = () => {
    if (tag) activeNotifications.delete(tag);
  };

  if (tag) activeNotifications.set(tag, notification);

  return notification;
};

const closeMessengerNotification = (tag) => {
  const notification = activeNotifications.get(tag);
  if (notification) {
    notification.close();
    activeNotifications.delete(tag);
  }
};

const playToneSequence = (notes, durationMs = 700, volume = 0.16) => {
  const AudioContextCtor = getAudioContextCtor();
  if (!AudioContextCtor) return null;

  try {
    const context = new AudioContextCtor();
    const masterGain = context.createGain();
    masterGain.gain.setValueAtTime(0.0001, context.currentTime);
    masterGain.gain.exponentialRampToValueAtTime(volume, context.currentTime + 0.02);
    masterGain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + durationMs / 1000);
    masterGain.connect(context.destination);

    notes.forEach(({ start, frequency, length = 0.12, type = 'sine' }) => {
      const oscillator = context.createOscillator();
      const noteGain = context.createGain();
      const startAt = context.currentTime + start;
      const endAt = startAt + length;

      oscillator.type = type;
      oscillator.frequency.setValueAtTime(frequency, startAt);
      noteGain.gain.setValueAtTime(0.0001, startAt);
      noteGain.gain.exponentialRampToValueAtTime(1, startAt + 0.015);
      noteGain.gain.exponentialRampToValueAtTime(0.0001, endAt);

      oscillator.connect(noteGain);
      noteGain.connect(masterGain);
      oscillator.start(startAt);
      oscillator.stop(endAt + 0.02);
    });

    window.setTimeout(() => {
      if (context.state !== 'closed') context.close().catch(() => {});
    }, durationMs + 80);

    return context;
  } catch (error) {
    return null;
  }
};

const playIncomingCallRingtoneOnce = () =>
  playToneSequence(
    [
      { start: 0, frequency: 659.25, length: 0.14 },
      { start: 0.18, frequency: 880, length: 0.14 },
      { start: 0.48, frequency: 659.25, length: 0.14 },
      { start: 0.66, frequency: 880, length: 0.14 },
    ],
    1200,
    0.18,
  );

const startIncomingCallRingtone = () => {
  if (typeof window === 'undefined') return;
  if (callRingtone) return;

  playIncomingCallRingtoneOnce();
  const intervalId = window.setInterval(playIncomingCallRingtoneOnce, 2400);
  callRingtone = { intervalId };
};

const stopIncomingCallRingtone = () => {
  if (typeof window === 'undefined' || !callRingtone) return;
  window.clearInterval(callRingtone.intervalId);
  callRingtone = null;
};

const playMessageNotificationSound = () =>
  playToneSequence(
    [
      { start: 0, frequency: 988, length: 0.08, type: 'triangle' },
      { start: 0.1, frequency: 1318.51, length: 0.1, type: 'triangle' },
    ],
    420,
    0.12,
  );

export {
  closeMessengerNotification,
  getMessengerNotificationPermission,
  playIncomingCallRingtoneOnce,
  playMessageNotificationSound,
  requestMessengerNotificationPermission,
  showMessengerNotification,
  startIncomingCallRingtone,
  stopIncomingCallRingtone,
};
